import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject, catchError, debounceTime, distinctUntilChanged, finalize, map, of, switchMap } from 'rxjs';
import moment from 'moment';
import { DashboardBaseComponent } from '../dashboard-operacional/dashboard-base';
import { KpiCard } from '../dashboard-operacional/dashboard-operacional.mock';
import { DashboardService } from 'src/app/services/DashboardService/dashboard.service';
import {
  IDashboardFinanceiroAnoMes,
  IDashboardFinanceiroItem,
  IGetDashboardFinanceiroResponse
} from 'src/app/services/DashboardService/dashboard.model';
import { StorageService } from 'src/app/services/StorageService/storage.service';

@Component({
  selector: 'app-dashboard-financeiro',
  templateUrl: './dashboard-financeiro.component.html',
  styleUrls: [
    '../dashboard-operacional/dashboard-operacional.component.scss',
    './dashboard-financeiro.component.scss'
  ]
})
export class DashboardFinanceiroComponent
  extends DashboardBaseComponent
  implements OnInit
{
  readonly dashboardTitle = 'Dashboard Financeiro';
  readonly pdfFileNamePrefix = 'Dashboard-Financeiro';
  readonly dashboardStorageKey = 'dashboard-financeiro-sections';

  readonly meses = [
    { mes: 1, nome: 'Janeiro' },
    { mes: 2, nome: 'Fevereiro' },
    { mes: 3, nome: 'Março' },
    { mes: 4, nome: 'Abril' },
    { mes: 5, nome: 'Maio' },
    { mes: 6, nome: 'Junho' },
    { mes: 7, nome: 'Julho' },
    { mes: 8, nome: 'Agosto' },
    { mes: 9, nome: 'Setembro' },
    { mes: 10, nome: 'Outubro' },
    { mes: 11, nome: 'Novembro' },
    { mes: 12, nome: 'Dezembro' }
  ];

  kpis: KpiCard[] = [];
  porDescricao: IDashboardFinanceiroItem[] = [];
  porTransportadora: IDashboardFinanceiroItem[] = [];
  porFormaPagamento: IDashboardFinanceiroItem[] = [];
  grupoOptions: { value: string; name: string }[] = [
    { value: 'Todos', name: 'Todos' }
  ];
  transportadoraOptions: { value: string; name: string }[] = [];
  anos: number[] = [];
  grupoForm: FormGroup;
  transportadoraForm: FormGroup;

  private quantidadePorAnoMes: IDashboardFinanceiroAnoMes[] = [];
  private matriz: Record<number, Record<number, { quantidade: number; valor: number }>> = {};
  private readonly loadTrigger$ = new Subject<void>();

  constructor(
    fb: FormBuilder,
    storageService: StorageService,
    private dashboardService: DashboardService
  ) {
    super(fb, storageService);
    const fim = moment().endOf('day');
    const inicio = moment().startOf('month').startOf('day');
    this.form.patchValue({
      DT_INIC: inicio.format('YYYY-MM-DDTHH:mm'),
      DT_FIM: fim.format('YYYY-MM-DDTHH:mm')
    });
    this.grupoForm = fb.group({
      DS_GRUPO: ['Todos']
    });
    this.transportadoraForm = fb.group({
      NM_TRND: [null as string | null]
    });
  }

  ngOnInit(): void {
    this.initSectionCollapse({
      descricao: false,
      transportadora: false,
      pagamento: false,
      anual: false
    });
    this.loadTrigger$
      .pipe(
        debounceTime(300),
        map(() => this.buildParamsKey()),
        distinctUntilChanged(),
        switchMap(() => {
          this.isLoading = true;
          return this.dashboardService
            .getDashboardFinanceiro({
              DT_INIC: this.form.controls['DT_INIC'].value,
              DT_FIM: this.form.controls['DT_FIM'].value,
              NM_TRND: this.transportadoraFiltroLabel || undefined
            })
            .pipe(
              catchError(() => of(null)),
              finalize(() => {
                this.isLoading = false;
              })
            );
        })
      )
      .subscribe(data => {
        if (data) {
          this.applyResponse(data);
        }
      });

    this.form.valueChanges.subscribe(() => this.loadTrigger$.next());
    this.transportadoraForm.valueChanges.subscribe(() => this.loadTrigger$.next());
    this.grupoForm.valueChanges.subscribe(() => this.atualizarMatriz());
    this.loadTrigger$.next();
  }

  quantidadeNoMes(ano: number, mes: number): number {
    return this.matriz[ano]?.[mes]?.quantidade ?? 0;
  }

  valorNoMes(ano: number, mes: number): string {
    return this.formatCurrency(this.matriz[ano]?.[mes]?.valor ?? 0);
  }

  get porDescricaoQuantidade(): { value: number }[] {
    return this.porDescricao.map(item => ({ value: item.Quantidade }));
  }

  get porDescricaoValor(): { value: number }[] {
    return this.porDescricao.map(item => ({ value: Number(item.Valor) }));
  }

  get transportadoraQuantidade(): { value: number }[] {
    return this.porTransportadora.map(item => ({ value: item.Quantidade }));
  }

  get transportadoraValor(): { value: number }[] {
    return this.porTransportadora.map(item => ({ value: Number(item.Valor) }));
  }

  get pagamentoValor(): { value: number }[] {
    return this.porFormaPagamento.map(item => ({ value: Number(item.Valor) }));
  }

  get transportadoraFiltroLabel(): string {
    const selecionada = this.transportadoraForm?.controls['NM_TRND']?.value;
    if (!selecionada || selecionada === 'Todos') {
      return '';
    }

    return selecionada;
  }

  private applyResponse(data: IGetDashboardFinanceiroResponse): void {
    if (!data) {
      return;
    }

    const quantidade = data.QuantidadeCaminhoes ?? 0;
    const valor = Number(data.ValorTotal ?? 0);

    this.kpis = [
      {
        label: 'Caminhões no período',
        value: quantidade.toLocaleString('pt-BR'),
        hint: 'Placas com recebimento'
      },
      {
        label: 'Valor no período',
        value: this.formatCurrency(valor),
        hint: 'Soma do pátio no período'
      }
    ];

    this.porDescricao = data.PorDescricao ?? [];
    this.porTransportadora = data.PorTransportadora ?? [];
    this.porFormaPagamento = data.PorFormaPagamento ?? [];
    this.quantidadePorAnoMes = data.QuantidadePorAnoMes ?? [];

    this.atualizarOpcoesTransportadora(data);

    const grupos = data.Grupos ?? [];
    this.grupoOptions = [
      { value: 'Todos', name: 'Todos' },
      ...grupos.map(grupo => ({ value: grupo, name: grupo }))
    ];

    const selecionado = this.grupoForm.controls['DS_GRUPO'].value;
    if (!this.grupoOptions.some(opcao => opcao.value === selecionado)) {
      this.grupoForm.patchValue({ DS_GRUPO: 'Todos' }, { emitEvent: false });
    }

    this.atualizarMatriz();
  }

  private buildParamsKey(): string {
    return [
      this.form.controls['DT_INIC'].value ?? '',
      this.form.controls['DT_FIM'].value ?? '',
      this.transportadoraFiltroLabel
    ].join('|');
  }

  private atualizarOpcoesTransportadora(data: IGetDashboardFinanceiroResponse): void {
    const selecionada = this.transportadoraFiltroLabel;
    const fromApi = Array.isArray(data.Transportadoras)
      ? data.Transportadoras.filter(nome => !!nome)
      : [];
    const fromChart = (data.PorTransportadora ?? [])
      .map(item => item.Label)
      .filter(nome => !!nome);
    const nomes = fromApi.length ? fromApi : fromChart;

    if (!selecionada && nomes.length) {
      this.setTransportadoraOptions(nomes);
      return;
    }

    if (selecionada && !this.transportadoraOptions.some(opcao => opcao.value === selecionada)) {
      this.setTransportadoraOptions([selecionada, ...nomes]);
    }
  }

  private setTransportadoraOptions(nomes: string[]): void {
    const unique = [...new Set(nomes.filter(Boolean))];
    const next = unique.map(nome => ({ value: nome, name: nome }));
    const same =
      next.length === this.transportadoraOptions.length &&
      next.every((opcao, index) => opcao.value === this.transportadoraOptions[index]?.value);

    if (!same) {
      this.transportadoraOptions = next;
    }
  }

  private atualizarMatriz(): void {
    const grupo = this.grupoForm.controls['DS_GRUPO'].value || 'Todos';
    const linhas = this.quantidadePorAnoMes.filter(item => item.Grupo === grupo);
    const anos = new Set<number>(linhas.map(item => item.Ano));
    anos.add(new Date().getFullYear());

    this.anos = [...anos].sort((a, b) => a - b);
    this.matriz = {};
    for (const ano of this.anos) {
      this.matriz[ano] = {};
    }
    for (const linha of linhas) {
      if (!this.matriz[linha.Ano]) {
        this.matriz[linha.Ano] = {};
      }
      this.matriz[linha.Ano][linha.Mes] = {
        quantidade: linha.Quantidade,
        valor: Number(linha.Valor ?? 0)
      };
    }
  }
}
